class WaveSection {
  constructor(section) {
    this.section = section;
    this.wave = section.querySelector('.wave');
    this.scrollSpeed = 1;
    this.rafId = null;
    this.enableAnimation = true;
    this.isInitialized = false;
    
    if (!this.wave) {
      console.warn('Wave element not found in section');
      return;
    }

    // Check if animation is enabled via data attribute
    const animationEnabled = this.section.dataset.enableAnimation;
    this.enableAnimation = animationEnabled !== 'false' && animationEnabled !== false;

    this.init();
  }

  init() {
    if (this.isInitialized) return;
    this.isInitialized = true;

    // Get scroll speed from CSS variable
    const scrollSpeedValue = getComputedStyle(this.section).getPropertyValue('--scroll-speed').trim();
    this.scrollSpeed = Number.parseFloat(scrollSpeedValue) || 1;

    // Initialize with current scroll position
    this.updateWavePosition();
    
    // Only set up scroll listeners if animation is enabled
    if (this.enableAnimation) {
      // Set up scroll listener with throttling
      this.scrollHandler = this.throttle(() => {
        this.updateWavePosition();
      });

      window.addEventListener('scroll', this.scrollHandler, { passive: true });
      window.addEventListener('resize', this.scrollHandler, { passive: true });
    } else {
      // If animation is disabled, set scroll offset to 0
      this.wave.style.setProperty('--scroll-offset', 0);
    }

    // Initial update to ensure wave is visible
    requestAnimationFrame(() => {
      this.updateWavePosition();
    });
  }

  updateWavePosition() {
    if (!this.wave) return;

    // If animation is disabled, keep offset at 0
    if (!this.enableAnimation) {
      this.wave.style.setProperty('--scroll-offset', 0);
      return;
    }

    // Get section position relative to viewport
    const rect = this.section.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    
    // Calculate scroll offset based on section's top position in viewport
    // Animation starts when the top of the section enters the viewport
    // When top is above viewport (rect.top > windowHeight), offset is 0
    // When top enters viewport (0 < rect.top <= windowHeight), offset increases
    // Stop scrolling when top reaches viewport top (rect.top <= 0)
    // Offset increases as the section moves up, capped at windowHeight when top hits viewport top
    const scrollOffset = Math.min(windowHeight, Math.max(0, windowHeight - rect.top));
    
    // Update CSS variable
    this.wave.style.setProperty('--scroll-offset', scrollOffset);
  }

  throttle(func) {
    let rafId = null;
    return (...args) => {
      if (rafId === null) {
        rafId = requestAnimationFrame(() => {
          func.apply(this, args);
          rafId = null;
        });
      }
    };
  }

  destroy() {
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
      window.removeEventListener('resize', this.scrollHandler);
    }
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
  }
}

// Store all wave section instances to prevent duplicates
const waveInstances = new Map();

// Initialize all wave sections
function initWaveSections() {
  const waveSections = document.querySelectorAll('.section-wave');
  
  waveSections.forEach((section) => {
    const sectionId = section.dataset.sectionId;
    
    // Skip if already initialized
    if (waveInstances.has(sectionId)) {
      return;
    }

    const instance = new WaveSection(section);
    if (instance.wave) {
      waveInstances.set(sectionId, instance);
    }
  });

  return Array.from(waveInstances.values());
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWaveSections);
} else {
  initWaveSections();
}

// Re-initialize when Shopify theme editor loads new sections
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const sectionId = event.detail.sectionId;
    const section = event.target.querySelector('.section-wave');
    
    if (section && sectionId) {
      // Remove old instance if it exists
      if (waveInstances.has(sectionId)) {
        const oldInstance = waveInstances.get(sectionId);
        oldInstance.destroy();
        waveInstances.delete(sectionId);
      }
      
      // Create new instance
      const instance = new WaveSection(section);
      if (instance.wave) {
        waveInstances.set(sectionId, instance);
      }
    }
  });

  // Clean up when section is unloaded
  document.addEventListener('shopify:section:unload', (event) => {
    const sectionId = event.detail.sectionId;
    if (waveInstances.has(sectionId)) {
      const instance = waveInstances.get(sectionId);
      instance.destroy();
      waveInstances.delete(sectionId);
    }
  });
}

